"use client";

import type { CopilotKeyScope, CopilotTeamOption } from "@/lib/api-keys/copilot-key-scope";
import { useEffect, useMemo, useRef, useState } from "react";
import { createClientApiKey } from "@/app/actions/admin/client-actions";
import { checkCopilotKeyTraffic, verifyCopilotGateway } from "@/app/actions/admin/copilot-connect-actions";
import { createTeam, listTeams } from "@/app/actions/admin/team-actions";
import { createTeamApiKey } from "@/app/actions/api-key-actions";
import { CommandBlock } from "@/components/api-keys/command-block";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { reserveSegmentClass } from "@/components/ui/reserve-workspace";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RECOMMENDED_COPILOT_MODEL_IDS } from "@/lib/ai/providers";
// Keys minted through this flow are tagged for usage/reporting filters.
import { COPILOT_KEY_CATEGORY } from "@/lib/api-keys/categories";
import { copilotKeyNameFor, copilotTeamOptions, DEFAULT_COPILOT_KEY_NAME, githubEntryNameFor, isSuggestedCopilotKeyName } from "@/lib/api-keys/copilot-key-scope";
import { PROD_BASE_URL } from "@/lib/audacity-api/constants";
import { DEVELOPER_GUIDE_ROUTES } from "@/lib/navigation/portal-routes";
import { AlertTriangle, CheckCircle2, ExternalLink, Loader2, Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";

/** Poll cadence and cap for the connection-traffic check. */
const TRAFFIC_POLL_INTERVAL_MS = 10_000;
const TRAFFIC_POLL_MAX_MS = 30 * 60 * 1000;

/**
 * Click-through paths in the customer's own GitHub settings. The org and
 * enterprise consoles differ (per GitHub docs, re-verified 2026-10-19): only the
 * enterprise console has "AI controls"; org admins go through org Settings.
 * GitHub's custom-models feature is a public preview, so wording may drift —
 * keep the steps in these constants so copy fixes are one-line.
 */
const GITHUB_ORG_ADMIN_PATH = "Organization Settings → Code, planning, and automation → Copilot → Models → Custom models tab → Add API key.";
const GITHUB_ENTERPRISE_ADMIN_PATH = "Enterprise → AI controls → Copilot → Configure custom models → Add API key.";
const GITHUB_SHARED_STEPS = ["Pick “OpenAI-compatible” as the provider.", "Paste the base URL and API key below, then add the model IDs you want to offer (copy them exactly — IDs are case-sensitive).", "Enable the models for your organizations. They appear at the bottom of developers' Copilot model picker (VS Code, JetBrains, github.com chat, Copilot CLI) within a few minutes — no per-developer setup."];
const GITHUB_ORG_SLUG_SHAPE = /^[a-zA-Z0-9-]+$/;
type VerifyState = "verifying" | "verified" | "failed";

/** The rows the department picker needs; `listTeams` and `createTeam` both return a superset. */
interface TeamRow {
  id: string;
  name: string;
  parentTeamId: string | null;
}

/** Select value that opens the inline "new department" form instead of picking a team. */
const NEW_TEAM_VALUE = "__new_department__";

/** One shape for both key scopes; the traffic poll only needs the key's id. */
async function mintCopilotKey(input: {
  scope: CopilotKeyScope;
  teamId: string | null;
  clientId: string;
  name: string;
}): Promise<{
  success: true;
  data: {
    apiKey: string;
    keyId: string;
  };
} | {
  success: false;
  error: string;
}> {
  if (input.scope === "team" && input.teamId) {
    const result = await createTeamApiKey(input.teamId, input.name, COPILOT_KEY_CATEGORY);
    return result.success ? {
      success: true,
      data: {
        apiKey: result.data.apiKey,
        keyId: result.data.id
      }
    } : {
      success: false,
      error: result.error
    };
  }
  const result = await createClientApiKey(input.clientId, input.name, COPILOT_KEY_CATEGORY);
  return result.success ? {
    success: true,
    data: {
      apiKey: result.data.apiKey,
      keyId: result.data.keyId
    }
  } : {
    success: false,
    error: result.error
  };
}
interface ConnectCopilotDialogProps {
  clientId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a key was created (fires when the dialog closes). */
  onCreated?: () => void | Promise<void>;
}

/**
 * Enterprise-admin onboarding for GitHub Copilot (AIR-543): mints a key
 * (category "copilot") and shows the values + steps the customer's GitHub
 * admin pastes into their own GitHub settings. The customer's developers
 * keep signing into their own GitHub org — the only artifact on our side is
 * this key.
 *
 * Who the key is for (2026-10-28): GitHub's custom-models path attributes
 * nothing per developer — every request arrives on the key — so the dialog
 * offers two scopes. "Whole organization" is the original one-key rollout.
 * "One department" mints a team-scoped key so the admin registers one GitHub
 * entry per department and spend lands on that team's budget; a prospect
 * read the one-key version as "Copilot can't split by department".
 *
 * The dialog verifies its own work: right after minting it fires a real
 * completion through the public gateway (tagged copilot-self-test) and only
 * shows "verified" on a confirmed 200, then polls the usage log until
 * non-self-test traffic lands on the key.
 */
export function ConnectCopilotDialog({
  clientId,
  open,
  onOpenChange,
  onCreated
}: ConnectCopilotDialogProps) {
  const [name, setName] = useState(DEFAULT_COPILOT_KEY_NAME);
  const [scope, setScope] = useState<CopilotKeyScope>("organization");
  const [teams, setTeams] = useState<TeamRow[] | null>(null);
  const [teamsError, setTeamsError] = useState<string | null>(null);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [newTeamOpen, setNewTeamOpen] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");
  const [newTeamParentId, setNewTeamParentId] = useState<string | null>(null);
  const [isCreatingTeam, setIsCreatingTeam] = useState(false);
  const teamOptions: CopilotTeamOption[] | null = useMemo(() => teams ? copilotTeamOptions(teams) : null, [teams]);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [keyId, setKeyId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [verifyState, setVerifyState] = useState<VerifyState>("verifying");
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [trafficDetected, setTrafficDetected] = useState(false);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const [githubOrg, setGithubOrg] = useState("");
  const pollStartRef = useRef<number | null>(null);
  useEffect(() => {
    if (!open) {
      setName(DEFAULT_COPILOT_KEY_NAME);
      setScope("organization");
      setTeams(null);
      setTeamsError(null);
      setTeamId(null);
      setNewTeamOpen(false);
      setNewTeamName("");
      setNewTeamParentId(null);
      setGeneratedKey(null);
      setKeyId(null);
      setVerifyState("verifying");
      setVerifyError(null);
      setTrafficDetected(false);
      setPollTimedOut(false);
      setGithubOrg("");
      pollStartRef.current = null;
    }
  }, [open]);

  // Teams load once per open, when the admin first asks for a department key.
  useEffect(() => {
    if (!open || scope !== "team" || teams !== null) return;
    let cancelled = false;
    void (async () => {
      const result = await listTeams(clientId);
      if (cancelled) return;
      if (result.success) {
        setTeams(result.data.map(({
          id,
          name: name_0,
          parentTeamId
        }) => ({
          id,
          name: name_0,
          parentTeamId
        })));
      } else {
        setTeamsError(result.error);
        setTeams([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, scope, teams, clientId]);
  const selectedTeam = teamOptions?.find(option => option.id === teamId) ?? null;
  const chooseScope = (next: CopilotKeyScope) => {
    setScope(next);
    if (isSuggestedCopilotKeyName(name)) {
      setName(copilotKeyNameFor(next, next === "team" ? selectedTeam?.name : null));
    }
  };
  const chooseTeam = (nextTeamId: string) => {
    if (nextTeamId === NEW_TEAM_VALUE) {
      setNewTeamOpen(true);
      return;
    }
    setNewTeamOpen(false);
    setTeamId(nextTeamId);
    const team = teamOptions?.find(option_0 => option_0.id === nextTeamId);
    if (isSuggestedCopilotKeyName(name)) {
      setName(copilotKeyNameFor("team", team?.name));
    }
  };

  // Creating the department here is the same action as People › Teams; the
  // new team joins the list and becomes the key's department straight away.
  const handleCreateTeam = async () => {
    const trimmed = newTeamName.trim();
    if (!trimmed) {
      toast.error("Department name is required");
      return;
    }
    setIsCreatingTeam(true);
    try {
      const result = await createTeam(clientId, {
        name: trimmed,
        parentTeamId: newTeamParentId
      });
      if (!result.success) {
        toast.error(result.error);
        return;
      }
      const created: TeamRow = {
        id: result.data.id,
        name: result.data.name,
        parentTeamId: result.data.parentTeamId
      };
      setTeams(current => [...(current ?? []), created]);
      setTeamId(created.id);
      if (isSuggestedCopilotKeyName(name)) setName(copilotKeyNameFor("team", created.name));
      setNewTeamOpen(false);
      setNewTeamName("");
      setNewTeamParentId(null);
      toast.success(`Department “${created.name}” created`);
    } finally {
      setIsCreatingTeam(false);
    }
  };
  const runSelfTest = async (apiKey: string) => {
    setVerifyState("verifying");
    setVerifyError(null);
    const result_0 = await verifyCopilotGateway(clientId, apiKey);
    if (result_0.success) {
      setVerifyState("verified");
    } else {
      setVerifyState("failed");
      setVerifyError(result_0.error);
    }
  };
  const handleCreate = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error("Key name is required");
      return;
    }
    if (scope === "team" && !teamId) {
      toast.error("Pick the department this key is for");
      return;
    }
    setIsCreating(true);
    try {
      const result = await mintCopilotKey({
        scope,
        teamId,
        clientId,
        name: trimmedName
      });
      if (result.success) {
        setGeneratedKey(result.data.apiKey);
        setKeyId(result.data.keyId);
        pollStartRef.current = Date.now();
        toast.success("Copilot key created");
        void runSelfTest(result.data.apiKey);
      } else {
        toast.error(result.error);
      }
    } finally {
      setIsCreating(false);
    }
  };

  // Poll for the first non-self-test request on the key. The self-test is
  // excluded server-side by its x-usage-purpose marker; a hit here means
  // something else used the key (very likely GitHub, but User-Agent isn't
  // logged, so the label stays "connection traffic detected").
  useEffect(() => {
    if (!open || !keyId || trafficDetected || pollTimedOut) return;
    let cancelled_0 = false;
    const poll = async () => {
      if (pollStartRef.current && Date.now() - pollStartRef.current > TRAFFIC_POLL_MAX_MS) {
        if (!cancelled_0) setPollTimedOut(true);
        return;
      }
      const result_1 = await checkCopilotKeyTraffic(clientId, keyId);
      if (!cancelled_0 && result_1.success && result_1.data.detected) {
        setTrafficDetected(true);
      }
    };
    void poll();
    const interval = setInterval(() => void poll(), TRAFFIC_POLL_INTERVAL_MS);
    return () => {
      cancelled_0 = true;
      clearInterval(interval);
    };
  }, [open, keyId, trafficDetected, pollTimedOut, clientId]);
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && generatedKey) {
      void onCreated?.();
    }
    onOpenChange(nextOpen);
  };
  const trimmedOrg = githubOrg.trim();
  const orgSettingsUrl = GITHUB_ORG_SLUG_SHAPE.test(trimmedOrg) ? `https://github.com/organizations/${trimmedOrg}/settings/copilot` : null;
  return <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Connect GitHub Copilot</DialogTitle>
          <DialogDescription>
            A key your GitHub admin pastes into your own GitHub enterprise or organization settings. Every Copilot
            request on it is attributed to whoever the key is for — the whole organization, or one department — and
            spends from that budget. Make sure the wallet is funded, or Copilot requests will fail. The key is only
            shown once — save it now.
          </DialogDescription>
        </DialogHeader>
        {!generatedKey ? <div className="grid gap-3 py-2">
            <div className="grid gap-2">
              <Label>Who is this key for?</Label>
              <div role="radiogroup" aria-label="Who is this key for?" className="flex w-fit gap-1 rounded-full border border-white/60 bg-white/30 p-1 dark:border-white/10 dark:bg-white/[0.05]">
                <button type="button" role="radio" aria-checked={scope === "organization"} className={reserveSegmentClass(scope === "organization")} onClick={() => chooseScope("organization")}>
                  Whole organization
                </button>
                <button type="button" role="radio" aria-checked={scope === "team"} className={reserveSegmentClass(scope === "team")} onClick={() => chooseScope("team")}>
                  One department
                </button>
              </div>
              <p className="text-muted-foreground text-xs">
                {scope === "organization" ? "One key for everyone. All Copilot spend lands on the organization; departments are not told apart." : "One key per department. Spend on it lands on that team and counts against its budget. Repeat this for each department and register each key as its own entry in GitHub."}
              </p>
            </div>
            {scope === "team" ? <div className="grid gap-2">
                <Label htmlFor="copilot-key-team">Department (team)</Label>
                <Select value={newTeamOpen ? NEW_TEAM_VALUE : teamId ?? ""} onValueChange={chooseTeam} disabled={teamOptions === null}>
                  <SelectTrigger id="copilot-key-team" className="w-full sm:max-w-sm">
                    <SelectValue placeholder={teamOptions === null ? "Loading teams…" : teamOptions.length === 0 ? "No departments yet — create the first one" : "Select the department"} />
                  </SelectTrigger>
                  <SelectContent>
                    {(teamOptions ?? []).map(option_1 => <SelectItem key={option_1.id} value={option_1.id}>
                        {option_1.label}
                      </SelectItem>)}
                    <SelectItem value={NEW_TEAM_VALUE}>
                      <span className="flex items-center gap-1.5">
                        <Plus className="h-3.5 w-3.5" />
                        Create a new department…
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                {teamsError ? <p className="text-destructive text-xs">{teamsError}</p> : null}
                {newTeamOpen ? <div className="grid gap-2 rounded-md border border-white/60 bg-white/30 p-3 dark:border-white/10 dark:bg-white/[0.05]">
                    <div className="grid gap-1.5">
                      <Label htmlFor="copilot-new-team-name" className="text-xs">
                        Department name
                      </Label>
                      <Input id="copilot-new-team-name" autoFocus placeholder="e.g. Network Security" value={newTeamName} onChange={e => setNewTeamName(e.target.value)} className="h-8 text-xs" />
                    </div>
                    {teamOptions && teamOptions.length > 0 ? <div className="grid gap-1.5">
                        <Label htmlFor="copilot-new-team-parent" className="text-xs">
                          Part of (optional)
                        </Label>
                        <Select value={newTeamParentId ?? "__none__"} onValueChange={value => setNewTeamParentId(value === "__none__" ? null : value)}>
                          <SelectTrigger id="copilot-new-team-parent" className="h-8 w-full text-xs sm:max-w-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">Top level</SelectItem>
                            {teamOptions.map(option_2 => <SelectItem key={option_2.id} value={option_2.id}>
                                {option_2.label}
                              </SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div> : null}
                    <div className="flex items-center gap-2">
                      <Button size="sm" className="h-8 text-xs" onClick={() => void handleCreateTeam()} disabled={isCreatingTeam || !newTeamName.trim()}>
                        {isCreatingTeam ? "Creating…" : "Create department"}
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => {
                setNewTeamOpen(false);
                setNewTeamName("");
              }}>
                        Cancel
                      </Button>
                    </div>
                    <p className="text-muted-foreground text-xs">
                      Same as creating it on People › Teams: members, budgets and reporting follow the team from here
                      on.
                    </p>
                  </div> : null}
              </div> : null}
            <div className="grid gap-2">
              <Label htmlFor="copilot-key-name">Key Name</Label>
              <Input id="copilot-key-name" autoFocus value={name} onChange={e_0 => setName(e_0.target.value)} />
              <p className="text-muted-foreground text-xs">
                Requires a GitHub Enterprise account with Copilot Business or Enterprise seats, Copilot enabled for the
                organization, and the “Enable custom models” policy turned on (custom models is a GitHub public
                preview). Revoking this key here cuts off Copilot access instantly.
              </p>
            </div>
            <p className="text-muted-foreground text-xs">
              Want spend per developer rather than per department? Each developer can connect Copilot with their own key
              through the{" "}
              <a href={DEVELOPER_GUIDE_ROUTES.copilot} target="_blank" rel="noreferrer" className="underline">
                AI Reserve extension
              </a>
              ; their usage then lands on them and their team automatically, with no GitHub admin step.
            </p>
          </div> : <div className="grid gap-3 py-2">
            {verifyState === "verifying" && <div className="bg-muted/50 text-muted-foreground flex items-center gap-2 rounded-md border p-2.5 text-xs">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Verifying — sending a test request through the gateway with this key…
              </div>}
            {verifyState === "verified" && <div className="flex items-center gap-2 rounded-md border border-green-600/30 bg-green-500/10 p-2.5 text-xs text-green-700 dark:text-green-400">
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                Gateway verified — a live request with this key returned a completion. Ready to paste into GitHub.
              </div>}
            {verifyState === "failed" && <div className="border-destructive/30 bg-destructive/10 text-destructive flex items-start gap-2 rounded-md border p-2.5 text-xs">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <div className="space-y-1.5">
                  <p>
                    Verification failed: {verifyError ?? "unknown error"}. The key below is active and saved — fix the
                    issue (e.g. fund the organization wallet), then retry.
                  </p>
                  <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs" onClick={() => void runSelfTest(generatedKey)}>
                    <RefreshCw className="h-3 w-3" />
                    Retry verification
                  </Button>
                </div>
              </div>}
            <div className="grid gap-1.5">
              <Label className="text-xs">Set up in GitHub (done by your GitHub admin)</Label>
              <ol className="text-muted-foreground list-decimal space-y-1 pl-5 text-xs">
                <li>
                  <span className="text-foreground font-medium">Organization admins:</span> {GITHUB_ORG_ADMIN_PATH}{" "}
                  <span className="text-foreground font-medium">Enterprise admins:</span> {GITHUB_ENTERPRISE_ADMIN_PATH}
                </li>
                {GITHUB_SHARED_STEPS.map(step => <li key={step}>{step}</li>)}
              </ol>
              <p className="text-muted-foreground text-xs">
                Don&apos;t see the Custom models tab? An enterprise owner must first enable the “Enable custom models”
                policy in GitHub Enterprise settings — standalone organizations without an enterprise can&apos;t use
                custom models yet (GitHub public preview).
              </p>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="copilot-github-org" className="text-xs">
                GitHub organization slug (optional — builds a direct link)
              </Label>
              <div className="flex items-center gap-2">
                <Input id="copilot-github-org" placeholder="my-org" value={githubOrg} onChange={e_1 => setGithubOrg(e_1.target.value)} className="h-8 text-xs" />
                {orgSettingsUrl && <Button asChild size="sm" variant="outline" className="h-8 shrink-0 gap-1.5 text-xs">
                    <a href={orgSettingsUrl} target="_blank" rel="noreferrer">
                      <ExternalLink className="h-3 w-3" />
                      Open Copilot settings
                    </a>
                  </Button>}
              </div>
            </div>
            <CommandBlock label="Base URL" command={`${PROD_BASE_URL}/v1`} copiedMessage="Base URL copied" hint="OpenAI-compatible endpoint, /v1 suffix included." />
            <CommandBlock label="API key" command={generatedKey} copiedMessage="API key copied" hint="Shown once. Paste into GitHub's API key field." />
            <CommandBlock label="Name for the GitHub entry" command={githubEntryNameFor(scope, selectedTeam?.name)} copiedMessage="Entry name copied" hint={scope === "team" ? "GitHub prefixes every model with this name, so developers can tell their department's entry apart." : "GitHub prefixes every model with this name in the picker."} />
            <CommandBlock label="Recommended model IDs" command={RECOMMENDED_COPILOT_MODEL_IDS.join(", ")} copiedMessage="Model IDs copied" hint="A curated coding subset — add these rather than everything the API lists. IDs are case-sensitive." />
            {trafficDetected ? <div className="flex items-center gap-2 rounded-md border border-green-600/30 bg-green-500/10 p-2.5 text-xs text-green-700 dark:text-green-400">
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                Connection traffic detected — a request beyond our self-test has used this key.
              </div> : pollTimedOut ? <p className="text-muted-foreground text-xs">
                No connection traffic seen yet. Models can take a few minutes to appear in Copilot after saving — check
                usage on this key later.
              </p> : <div className="bg-muted/50 text-muted-foreground flex items-center gap-2 rounded-md border p-2.5 text-xs">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Waiting for connection traffic on this key (updates automatically once GitHub — or anything else — sends
                its first request)…
              </div>}
            <p className="text-muted-foreground text-xs">
              Shareable instructions (no key) live at{" "}
              <a href={DEVELOPER_GUIDE_ROUTES.copilot} target="_blank" rel="noreferrer" className="underline">
                Coding Agents › GitHub Copilot
              </a>
              .
            </p>
          </div>}
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            {generatedKey ? "Done" : "Cancel"}
          </Button>
          {!generatedKey && <Button onClick={handleCreate} disabled={isCreating || !name.trim()}>
              {isCreating ? "Creating..." : "Create Key"}
            </Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>;
}